/**
 * Manual, user-triggered opportunity radar (human-centered design).
 *
 * The radar is a heavy cross-topic scan, and it is only meaningful AFTER the
 * user has gathered draft / pain signals in Discovery & Review. So it runs ONLY
 * when `run()` is called — never on mount — and once a result exists, a change
 * in `signalCount` (new signals in the review queue) marks it `isStale` so the
 * UI can suggest re-running. It never auto-reruns. Race-guarded against rapid
 * re-clicks; uses a generous timeout since the scan spans every topic.
 */
import { useCallback, useRef, useState } from "react";

import { fetchOpportunityRadar, type OpportunityRadarEntry } from "../services/api";

export type AnalysisStatus = "idle" | "loading" | "ok" | "error";

export interface ManualRadar {
  status: AnalysisStatus;
  radar: OpportunityRadarEntry[];
  disclaimer: string;
  error: string | null;
  /** Local time of the last successful run, or null. */
  ranAt: string | null;
  /** signalCount changed since the last successful run. */
  isStale: boolean;
  /** Absolute number of signal-count changes since the last run (for the nudge). */
  staleCount: number;
  run: () => void;
}

const ANALYSIS_TIMEOUT_MS = 30_000;

export function useManualRadar(signalCount: number): ManualRadar {
  const [status, setStatus] = useState<AnalysisStatus>("idle");
  const [radar, setRadar] = useState<OpportunityRadarEntry[]>([]);
  const [disclaimer, setDisclaimer] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [ranAt, setRanAt] = useState<string | null>(null);
  const ranAtCount = useRef<number | null>(null);
  const reqId = useRef(0);

  const run = useCallback(() => {
    const id = ++reqId.current;
    setStatus("loading");
    setError(null);
    void (async () => {
      try {
        const r = await fetchOpportunityRadar(ANALYSIS_TIMEOUT_MS);
        if (id !== reqId.current) return;
        setRadar(r.radar);
        setDisclaimer(r.disclaimer);
        ranAtCount.current = signalCount;
        setRanAt(new Date().toLocaleTimeString());
        setStatus("ok");
      } catch (e) {
        if (id !== reqId.current) return;
        setError(e instanceof Error ? e.message : String(e));
        setStatus("error");
      }
    })();
  }, [signalCount]);

  const isStale =
    status === "ok" && ranAtCount.current !== null && signalCount !== ranAtCount.current;
  const staleCount = isStale ? Math.abs(signalCount - (ranAtCount.current ?? signalCount)) : 0;

  return { status, radar, disclaimer, error, ranAt, isStale, staleCount, run };
}
